import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
	PlusOutlined,
	MinusOutlined,
	CloseOutlined,
	SendOutlined,
	CheckOutlined,
	HeartOutlined,
	StopOutlined,
} from "@ant-design/icons";
import { List, message, Dropdown, Input } from "antd";
import RationaleModal from "./RationaleModal.tsx";
import {
	CheckCircleOutlined,
	CommentOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	CarryOutOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { creatorListAPI, CreatorAssignmentApproval } from "@/api/creatorList.ts";
import { getPlatformNameFromUrl } from "@/shared/utils/platformLinks.ts";
import {
	CREATOR_CARD_NO_DATA_TEXT,
	formatEngagementRate,
	formatPercentage,
	isUnavailableCreatorCardMetric,
} from "@/shared/utils/socialProfile.ts";
import { formatNumberCommas } from "@/shared/utils/formatters.ts";
import { formatRateDisplayValue, isRateInputValid, sanitizeRateInputValue } from "@/shared/utils/rateInput.ts";
import { Icon, type IconName } from "@/shared/components/Icon/index.tsx";
import { CREATOR_CATEGORIES, CREATOR_TIERS } from "@/api/creator.ts";
import { CreatorInterestSelect } from "./CreatorInterestSelect.tsx";
import { StyledSelect } from "@/shared/components/Select/styledSelect.tsx";
import { useFinalRateEditing } from "../../hooks/useFinalRateEditing.ts";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar.tsx";
import { normalizeCreatorInterestStatus } from "../../utils/interestStatusHelper.ts";
import type { CreatorInterestStatus } from "@/shared/types/campaign";
import type { Creator } from "@/shared/types/campaign/creator";
import { useAssignmentContactEmail } from "../../hooks/useAssignmentContactEmail";

const GridContainer = styled.div`
	width: 100%;
	margin-top: 1rem;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
	width: 100%;
	gap: 24px;
	justify-content: center;
`;

const Card = styled.div<{ $isVisible: boolean; $isDragging?: boolean; $isDragOver?: boolean }>`
	background: ${(props) => (props.$isVisible ? "#FFFFFF" : "#E6E6E6")};
	border-radius: 24px;
	border: 1px solid #eaeaea;
	box-shadow: 0px 4px 12px rgba(21, 89, 165, 0.04);
	overflow: hidden;
	width: 100%;
	aspect-ratio: 370 / 500;
	display: flex;
	flex-direction: column;
	transition: background 0.2s;
	cursor: ${(props) => (props.$isDragging ? "grabbing" : "default")};
	opacity: ${(props) => (props.$isDragging ? 0.3 : 1)};
`;

const CardHeader = styled.div`
	padding: 24px 24px 16px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	position: relative;
`;

const CardHeaderTop = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 16px;
	width: 100%;
`;

const ProfileImage = styled(ResponsiveAvatar)`
	width: 80px;
	height: 80px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const CreatorInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
	min-width: 0;
`;

const NameRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: space-between;
	width: 100%;
`;

const NameWrap = styled.div`
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	align-items: center;
`;

const CreatorName = styled.h3`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 20px;
	font-weight: 700;
	line-height: 30px;
	color: #303030;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ProfileLinkButton = styled.a`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
	text-decoration: none;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: #4460ef;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
		color: #4460ef;
	}
`;

const TierCategoryRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
`;

const Category = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 12px;
	font-weight: 400;
	line-height: 18px;
	color: #707070;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const SettingsIcon = styled(Icon)<{ $isCustomizing?: boolean }>`
	width: 20px;
	height: 20px;
	cursor: ${(props) => (props.$isCustomizing ? "grab" : "pointer")};
	opacity: ${(props) => (props.$isCustomizing ? 1 : 0.6)};
	transition: opacity 0.2s;
	flex-shrink: 0;
	filter: ${(props) =>
		props.$isCustomizing
			? "invert(36%) sepia(84%) saturate(2572%) hue-rotate(315deg) brightness(98%) contrast(96%)"
			: "none"};

	&:hover {
		opacity: 1;
	}

	&:active {
		cursor: ${(props) => (props.$isCustomizing ? "grabbing" : "pointer")};
	}
`;

const TabsContainer = styled.div<{ $isVisible: boolean }>`
	display: flex;
	border-bottom: 1px solid #eaeaea;
	background: ${(props) => (props.$isVisible ? "#FFFFFF" : "#E6E6E6")};
	padding: 0 24px;
	transition: background 0.2s;
	overflow-x: auto;
	flex-wrap: nowrap;
`;

const Tab = styled.button<{ $active: boolean; $isVisible: boolean }>`
	flex: 0 0 60px;
	width: 60px;
	padding: 12px 8px;
	height: 62px;
	border: none;
	background: transparent;
	color: ${(props) => (props.$active ? "#F72586" : "#9B9B9B")};
	font-family: "Poppins", sans-serif;
	font-size: 12px;
	font-weight: 400;
	line-height: 18px;
	cursor: pointer;
	border-bottom: 2px solid ${(props) => (props.$active ? "#F72586" : "transparent")};
	transition: all 0.2s;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	&:hover {
		color: #f72586;
	}
`;

const TabIcon = styled(Icon)<{ $active: boolean }>`
	width: 16px;
	height: 16px;
	opacity: ${(props) => (props.$active ? 1 : 0.6)};
	transition: all 0.2s;
`;

const CardContent = styled.div`
	padding: 16px 24px;
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	min-height: 0;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb {
		background: #81a7e8;
		border-radius: 4px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: #6a92d4;
	}

	/* Firefox scrollbar styling */
	scrollbar-width: thin;
	scrollbar-color: #81a7e8 #f1f1f1;
`;

const ContentRow = styled.div`
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	gap: 12px;
	margin-bottom: 8px;
	align-items: start;

	&:last-child {
		margin-bottom: 0;
	}
`;

const ContentLabel = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #707070;
`;

const ContentSeparator = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #707070;
`;

const ContentValue = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #303030;
	word-break: break-word;
	min-width: 0;
	width: 100%;
`;

const ContactEmailField = styled.input<{ $invalid?: boolean }>`
	box-sizing: border-box;
	width: 100%;
	max-width: 200px;
	height: 26px;
	padding: 0 10px;
	border: 1px solid ${(props) => (props.$invalid ? "#d30024" : "#cdd4f6")};
	border-radius: 8px;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	color: #303030;
	background: #fff;
	transition: border-color 0.2s;

	&:focus,
	&:hover {
		outline: none;
		border-color: ${(props) => (props.$invalid ? "#d30024" : "#8193f1")};
	}
`;

const ContactEmailError = styled.span`
	display: block;
	color: #d30024;
	font-size: 11px;
	line-height: 16px;
`;

const CardFooter = styled.div<{ $isVisible: boolean }>`
	box-sizing: border-box;
	padding: 12px 24px;
	border-top: 1px solid #e5e5e5;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: center;
	min-height: 72px;
	flex-shrink: 0;
	background: ${(props) => (props.$isVisible ? "#FFFFFF" : "#E6E6E6")};
	transition: background 0.2s;
`;

const VisibleLabel = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: #454545;
	white-space: nowrap;
`;

const WriteRationaleButton = styled.button`
	display: flex;
	align-items: center;
	gap: 4px;
	height: 24px;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: #454545;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}
`;

const ClientActionText = styled.span`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
	height: 24px;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: #454545;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.8;
	}
`;

const ApprovalPopup = styled.div`
	display: flex;
	flex-direction: row;
	width: 373px;
	height: 84px;
	background: #ffffff;
	border-radius: 24px;
	padding: 8px;
	gap: 8px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
`;

const ApprovalOption = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1;
	height: 100%;
	gap: 4px;
	background: #fafafa;
	border-radius: 16px;
	cursor: pointer;
	transition: background 0.15s;

	&:hover {
		background: #f0f0f0;
	}

	span.label {
		font-family: "Poppins", sans-serif;
		font-size: 13px;
		font-weight: 700;
		color: #1a1a1a;
	}
`;

const SmallToggle = styled.div<{ $active: boolean; $disabled?: boolean }>`
	width: 36px;
	height: 20px;
	border-radius: 10px;
	background: ${(props) => (props.$active ? "#4460ef" : "#d9d9d9")};
	position: relative;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
	transition:
		background 0.2s,
		opacity 0.2s;
	flex-shrink: 0;

	&::after {
		content: "";
		position: absolute;
		top: 2px;
		left: ${(props) => (props.$active ? "18px" : "2px")};
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: white;
		transition: left 0.2s;
	}
`;

const ToggleWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	height: 24px;
`;

// Centered reachout-status shown in the client's outreach card footer.
const ClientReachoutStatus = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	height: 24px;
	font-family: "Poppins", sans-serif;
	font-size: 15px;
	font-weight: 600;
	color: #262626;

	.anticon {
		font-size: 20px;
	}
`;

// Icon + color per reachout status for the client outreach footer.
const REACHOUT_STATUS_DISPLAY: Record<
	CreatorInterestStatus | "none",
	{ label: string; color: string; icon: React.ReactNode }
> = {
	interested: { label: "Interested", color: "#7C3AED", icon: <HeartOutlined /> },
	"not-interested": { label: "Not interested", color: "#C2025B", icon: <StopOutlined /> },
	outreached: { label: "Outreached", color: "#1677ff", icon: <SendOutlined /> },
	negotiating: { label: "Negotiating", color: "#fa8c16", icon: <ClockCircleOutlined /> },
	none: { label: "Pending", color: "#8c8c8c", icon: <ClockCircleOutlined /> },
};

const AddToListButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.75rem 1.25rem;
	background: #4460ef;
	border: none;
	border-radius: 8px;
	color: white;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: background 0.2s;

	&:hover {
		background: #3651d9;
	}

	svg {
		font-size: 16px;
	}
`;

const RemoveFromListButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.75rem 1.25rem;
	background: white;
	border: 1px solid #4460ef;
	border-radius: 8px;
	color: #4460ef;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: #f0f5ff;
	}

	svg {
		font-size: 16px;
	}
`;

const PanelActionsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	width: 100%;
`;

const PanelToggleWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	width: 100%;
`;

const CreatorHeaderBottom = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const ClientApprovalBadge = styled.button<{ $status: "approved" | "rejected" | "pending" | "none" }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	height: 28px;
	border-radius: 999px;
	font-family: "Poppins", sans-serif;
	font-size: 13px;
	font-weight: 500;
	line-height: 18px;
	color: #333;
	white-space: nowrap;
	cursor: pointer;
	flex-shrink: 0;
	transition: opacity 0.2s;

	.anticon {
		font-size: 13px;
		color: ${(props) =>
			props.$status === "approved" ? "#66B026E0" : props.$status === "rejected" ? "#D80027" : "#8c8c8c"};
	}

	${(props) => {
		switch (props.$status) {
			case "approved":
				return `
					background: #f5f5f5;
					border: 1px solid #e0e0e0;
				`;
			case "rejected":
				return `
					background: #fde8ec;
					border: 1px solid #f5c6cf;
				`;
			case "pending":
				return `
					background: #f5f5f5;
					border: 1px solid #e0e0e0;
				`;
			case "none":
			default:
				return `
					background: #ffffff;
					color: #8c8c8c;
					border: 1px dashed #d9d9d9;
					cursor: default;
				`;
		}
	}}

	&:hover {
		${(props) => (props.$status === "none" ? "" : "opacity: 0.85;")}
	}
`;

const ClientCommentReadOnlyContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	gap: 12px;
`;

const ClientCommentReadOnlyBody = styled.div`
	flex: 1;
	border-radius: 16px;
	border: 1px solid #eaeaea;
	background: #fafafa;
	padding: 16px;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	line-height: 22px;
	color: #303030;
	white-space: pre-wrap;
	overflow-y: auto;
	min-height: 120px;
`;

const ClientCommentEmptyState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	color: #8c8c8c;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-style: italic;
	min-height: 120px;
	border-radius: 16px;
	border: 1px dashed #eaeaea;
	background: #fafafa;
`;

const ClientCommentAuthorRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-family: "Poppins", sans-serif;
	font-size: 12px;
	color: #707070;
`;

const ClientCommentAuthorAvatar = styled(ResponsiveAvatar)`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const SeeRecommendationsButton = styled.button<{ $isActive?: boolean }>`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 0;
	height: 24px;
	background: transparent;
	border: none;
	cursor: pointer;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: ${(props) => (props.$isActive ? "#F72586" : "#4460EF")};
	transition: color 0.2s;

	&:hover {
		opacity: 0.8;
	}
`;

const RecommendationIcon = styled.div<{ $isActive?: boolean }>`
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => (props.$isActive ? "#F72586" : "#4460ef")};
	transition: color 0.2s;
`;

const ChannelsSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	align-items: center;
	width: 100%;
	padding-bottom: 16px;
	border-bottom: 1px dashed #d3d3d3;
`;

const ChannelsLabel = styled.span`
	font-size: 14px;
	color: #666;
	font-weight: 500;
	text-align: center;
`;

const ChannelsIcons = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const ChannelIcon = styled.a<{ $platform: string }>`
	width: 32px;
	height: 32px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.2s;
	text-decoration: none;
	flex-shrink: 0;

	&:hover {
		opacity: 0.8;
	}

	${(props) => {
		switch (props.$platform.toLowerCase()) {
			case "tiktok":
				return "background: #000000;";
			case "instagram":
				return "background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);";
			case "facebook":
				return "background: #1877f2;";
			case "youtube":
				return "background: #ff0000;";
			default:
				return "background: #d9d9d9;";
		}
	}}
`;

const ChannelIconImage = styled(Icon)`
	width: 32px;
	height: 32px;
	object-fit: contain;
`;

const CommentBoxDivider = styled.div`
	border-top: 1px dashed #d3d3d3;
`;

const CommentBoxContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const CommentBoxHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 0 12px;
`;

const CommentBoxTitle = styled.h3`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #5c5c5c;
`;

const CommentBoxCloseButton = styled.button`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #303030;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}
	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`;

const CommentBoxTextAreaWrapper = styled.div`
	position: relative;
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

const CommentBoxTextArea = styled(Input.TextArea)`
	flex: 1 !important;
	height: 100% !important;
	max-height: none !important;
	border-radius: 16px !important;
	border: 1px solid #eaeaea !important;
	background: #ffffff !important;
	box-shadow: 0px 0px 10px 0px rgba(131, 170, 185, 0.31) !important;
	padding: 16px !important;
	padding-bottom: 40px !important;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	resize: none !important;

	&:focus {
		border-color: #4460ef !important;
		box-shadow: 0px 0px 10px 0px rgba(131, 170, 185, 0.31) !important;
	}

	&.ant-input {
		height: 100% !important;
	}
`;

const CommentBoxSendIcon = styled.button`
	position: absolute;
	bottom: 12px;
	right: 16px;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	color: #4460ef;
	font-size: 20px;
	transition: color 0.2s;

	&:hover {
		color: #3350d4;
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
	}
`;

const RecommendationsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	flex: 1;
	overflow-y: auto;
`;

const RecommendationsDivider = styled.div`
	border-top: 1px dashed #d3d3d3;
`;

const RecommendationsHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 0 12px;
`;

const RecommendationsTitle = styled.h3`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 16px;
	font-weight: 700;
	color: #303030;
`;

const RecommendationsCloseButton = styled.button`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	color: #303030;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}
`;

const RecommendationStatus = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 4px;
`;

const RecommendationStatusText = styled.span`
	font-size: 14px;
	font-weight: 700;
	line-height: 24px;
	color: #303030;
`;

const RecommendationBody = styled.div`
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	color: #303030;
	padding-left: 32px;
`;

const FooterRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
`;

const RateInputShell = styled.div<{ $invalid?: boolean }>`
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
	border: 1px solid ${(props) => (props.$invalid ? "#ff4d4f" : "#cdd4f6")};
	border-radius: 8px;
	background: #fff;
	transition:
		border-color 0.2s,
		box-shadow 0.2s;

	&:focus-within,
	&:hover {
		border-color: ${(props) => (props.$invalid ? "#ff4d4f" : "#8193f1")};
	}
`;

const RateSuffix = styled.span`
	position: absolute;
	right: 10px;
	font-size: 12px;
	color: #666;
	pointer-events: none;
`;

const RateTextInput = styled.input`
	width: 100%;
	height: 24px;
	border: none;
	outline: none;
	font-size: 14px;
	color: #303030;
	padding: 0 42px 0 10px;
	border-radius: 8px;
`;

interface CreatorGridProps {
	creators: Creator[];
	isCustomizing: boolean;
	creatorVisibility: { [key: string]: boolean };
	onCreatorVisibilityToggle: (creatorId: string) => void;
	onCreatorReorder?: (reorderedCreators: Creator[]) => void;
	onCommentClick?: (creatorId: string) => void;
	onSettingsClick?: (creatorId: string) => void;
	isClientOnly?: boolean;
	isOutreach?: boolean;
	canManage?: boolean;
	onInterestChange?: (creatorId: string, interested: CreatorInterestStatus | undefined) => void;
	isInPanel?: boolean;
	onAddToList?: (creatorId: string) => void;
	onRemoveFromList?: (creatorId: string) => void;
	campaignId?: string;
	blockId?: string;
	outreachId?: string;
	finalRateCurrency?: string;
	finalRateConvertedCurrency?: string;
	onRemoveFromCampaign?: (assignmentId: string, creatorName?: string) => void;
	onCategoryTierChange?: (creatorId: string, field: "categories" | "tier", value: string[] | string) => void;
	onUpdateCreator?: (update: { id: string; recommendation?: string[] }) => void;
	onContactEmailChange?: (assignmentId: string, contactEmail: string | null) => void;
}

type TabType = "basic" | "tiktok" | "instagram" | "facebook" | "youtube";

const PLATFORM_TABS: Record<Exclude<TabType, "basic">, string> = {
	tiktok: "TikTok",
	instagram: "Instagram",
	facebook: "Facebook",
	youtube: "YouTube",
};

const RESERVED_BASIC_FIELDS = new Set([
	"id",
	"name",
	"img_url",
	"tiktok",
	"instagram",
	"facebook",
	"youtube",
	"interested",
	"inList",
	"campaignId",
	"blockId",
	"assignment",
	"assignmentId",
	"userId",
	"contactEmail",
	"recommendation",
	"socialAccounts",
	"creatorInterested",
]);

const HIDDEN_RATE_PATTERNS = [
	"finalratenumber",
	"defaultratenumber",
	"finalratecurrency",
	"defaultratecurrency",
	"finalrateconvertedcurrency",
	"defaultrateconvertedcurrency",
	"finalrate",
	"defaultrate",
	"finalconvertedrate",
];

const isHiddenRateField = (key: string): boolean => {
	const normalized = key.toLowerCase().replace(/[\s_-]/g, "");
	return HIDDEN_RATE_PATTERNS.some((p) => normalized === p);
};

const HUMAN_LABEL_CACHE = new Map<string, string>();

const formatLabel = (key: string) => {
	if (HUMAN_LABEL_CACHE.has(key)) {
		return HUMAN_LABEL_CACHE.get(key)!;
	}
	const label = key
		.replace(/([A-Z])/g, " $1")
		.replace(/[_-]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase());
	HUMAN_LABEL_CACHE.set(key, label);
	return label;
};

const isRenderableValue = (value: any) => {
	if (value === null || value === undefined) {
		return false;
	}
	if (typeof value === "string" && value.trim() === "") {
		return false;
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	if (typeof value === "object") {
		return Object.keys(value).length > 0;
	}
	return true;
};

const formatDisplayValue = (key: string, value: any) => {
	if (isUnavailableCreatorCardMetric(key, value)) {
		return CREATOR_CARD_NO_DATA_TEXT;
	}

	if (!isRenderableValue(value)) {
		return "N/A";
	}

	if (Array.isArray(value)) {
		if (value.length === 0) {
			return "N/A";
		}
		if (typeof value[0] === "object" && value[0] !== null) {
			return value
				.map((entry) => {
					if ("ageRange" in entry && "percentage" in entry) {
						return `${entry.ageRange}: ${formatPercentage(Number(entry.percentage))}`;
					}
					return JSON.stringify(entry);
				})
				.join(", ");
		}
		return value.join(", ");
	}

	if (typeof value === "number") {
		const lowerKey = key.toLowerCase();
		if (lowerKey === "avgengagementrate") {
			return formatEngagementRate(value);
		}
		if (lowerKey.includes("percentage") || lowerKey.includes("rate")) {
			return formatPercentage(value);
		}
		return formatNumberCommas(value);
	}

	// Handle numeric strings (e.g. "92656256") by formatting as numbers
	if (typeof value === "string") {
		// Usernames are identifiers, not metrics. Facebook profile IDs can be
		// entirely numeric and must be displayed exactly as received.
		if (key.toLowerCase().includes("username")) {
			return value;
		}

		const cleaned = value.replace(/,/g, "").trim();
		if (/^-?\d+(?:\.\d+)?$/.test(cleaned)) {
			const n = Number(cleaned);
			const lowerKey = key.toLowerCase();
			if (lowerKey === "avgengagementrate") {
				return formatEngagementRate(n);
			}
			if (lowerKey.includes("percentage") || lowerKey.includes("rate")) {
				return formatPercentage(n);
			}
			return formatNumberCommas(n);
		}
	}

	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	return String(value);
};

const looksLikeUrl = (value: string) => /^https?:\/\//i.test(value);

export const CreatorGrid: React.FC<CreatorGridProps> = ({
	creators,
	isCustomizing,
	creatorVisibility,
	onCreatorVisibilityToggle,
	onCreatorReorder,
	onSettingsClick,
	isClientOnly = false,
	isOutreach = false,
	canManage = false,
	onInterestChange,
	isInPanel = false,
	onAddToList,
	onRemoveFromList,
	campaignId,
	blockId,
	outreachId,
	finalRateCurrency = "USD",
	finalRateConvertedCurrency = "AUD",
	onRemoveFromCampaign: _onRemoveFromCampaign,
	onCategoryTierChange,
	onUpdateCreator,
	onContactEmailChange,
}) => {
	const [searchParams] = useSearchParams();
	const initialTabs = useMemo(
		() =>
			creators.reduce<{ [key: string]: TabType }>(
				(acc, creator) => ({
					...acc,
					[creator.id]: acc[creator.id] || "basic",
				}),
				{},
			),
		[creators],
	);
	const [activeTabs, setActiveTabs] = useState<{ [key: string]: TabType }>(initialTabs);
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	const [approvals, setApprovals] = useState<{ [assignmentId: string]: CreatorAssignmentApproval | null }>({});
	const [loadingApprovals, setLoadingApprovals] = useState<{ [assignmentId: string]: boolean }>({});
	const [approvalDropdownOpen, setApprovalDropdownOpen] = useState<{ [creatorId: string]: boolean }>({});
	const [commentTexts, setCommentTexts] = useState<{ [creatorId: string]: string }>({});
	const [submittingComment, setSubmittingComment] = useState<{ [creatorId: string]: boolean }>({});

	const [clientApprovals, setClientApprovals] = useState<{
		[assignmentId: string]: CreatorAssignmentApproval | null;
	}>({});

	type CardMode = "basic" | "comment" | "client-comment" | "recommendations";
	const [cardModes, setCardModes] = useState<{ [creatorId: string]: CardMode }>({});
	const getCardMode = (creatorId: string): CardMode => cardModes[creatorId] || "basic";
	const setCardMode = (creatorId: string, mode: CardMode) => setCardModes((prev) => ({ ...prev, [creatorId]: mode }));
	const [rationale, setRationale] = useState<null | { creatorId: string; assignmentId: string; text?: string }>(null);
	const [submittingRationale, setSubmittingRationale] = useState(false);
	const [pendingPanelActions, setPendingPanelActions] = useState<{ [creatorId: string]: boolean }>({});
	const [panelInListOverrides, setPanelInListOverrides] = useState<{ [creatorId: string]: boolean }>({});
	const contactEmail = useAssignmentContactEmail(campaignId, blockId, onContactEmailChange);
	const {
		rateDrafts,
		setRateDrafts,
		convertedRateDrafts,
		setConvertedRateDrafts,
		rateInvalid,
		setRateInvalid,
		convertedRateInvalid,
		setConvertedRateInvalid,
		debouncedSaveFinalRates,
	} = useFinalRateEditing({
		creators,
		campaignId,
		blockId,
		creatorVisibility,
		finalRateCurrency,
		finalRateConvertedCurrency,
	});

	useEffect(() => {
		setActiveTabs((prev) => {
			const next = { ...prev };
			let changed = false;
			creators.forEach((creator) => {
				if (!next[creator.id]) {
					next[creator.id] = "basic";
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [creators]);

	useEffect(() => {
		if (!isInPanel) {
			return;
		}

		setPanelInListOverrides((prev) => {
			const next: { [creatorId: string]: boolean } = {};
			let changed = false;

			// Build a Set once → O(n)
			const creatorIdSet = new Set(creators.map((c) => c.id));

			// Build next state
			creators.forEach((creator) => {
				const value = prev[creator.id] ?? creator.inList === true;
				next[creator.id] = value;

				if (prev[creator.id] !== value) {
					changed = true;
				}
			});

			// Cleanup removed creators → now O(n)
			Object.keys(prev).forEach((creatorId) => {
				if (!creatorIdSet.has(creatorId)) {
					changed = true;
				}
			});

			return changed ? next : prev;
		});
	}, [creators, isInPanel]);

	// Fetch approvals for client users only
	useEffect(() => {
		if (!isClientOnly) return;

		const campaignId = searchParams.get("campaignId");
		const blockId = searchParams.get("blockId");

		if (!campaignId || !blockId) return;

		const fetchApprovals = async () => {
			for (const creator of creators) {
				const assignmentId = creator.assignment?.id || creator.assignmentId;
				if (!assignmentId || approvals[assignmentId] !== undefined || loadingApprovals[assignmentId]) {
					continue;
				}

				setLoadingApprovals((prev) => ({ ...prev, [assignmentId]: true }));
				try {
					const approval = await creatorListAPI.getCreatorAssignmentApprovalByUser(
						campaignId,
						blockId,
						assignmentId,
					);
					setApprovals((prev) => ({ ...prev, [assignmentId]: approval }));
				} catch (error) {
					console.error(`Error fetching approval for assignment ${assignmentId}:`, error);
					setApprovals((prev) => ({ ...prev, [assignmentId]: null }));
				} finally {
					setLoadingApprovals((prev) => {
						const newState = { ...prev };
						delete newState[assignmentId];
						return newState;
					});
				}
			}
		};

		fetchApprovals();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [creators, isClientOnly]);

	useEffect(() => {
		if (isClientOnly || !canManage) return;

		// Prefer the IDs passed as props (e.g. from CreatorDisplayLayout) and
		// fall back to the URL params so approvals still load when the component
		// is rendered without matching search params.
		const resolvedCampaignId = campaignId ?? searchParams.get("campaignId");
		const resolvedBlockId = blockId ?? searchParams.get("blockId");
		if (!resolvedCampaignId || !resolvedBlockId) return;

		let cancelled = false;
		const targets = creators.filter((creator) => {
			const assignmentId = creator.assignment?.id || creator.assignmentId;
			return !!assignmentId && clientApprovals[assignmentId] === undefined;
		});

		if (targets.length === 0) return;

		Promise.all(
			targets.map(async (creator) => {
				const assignmentId = (creator.assignment?.id || creator.assignmentId)!;
				try {
					const list = await creatorListAPI.getCreatorAssignmentApprovals(
						resolvedCampaignId,
						resolvedBlockId,
						assignmentId,
					);
					const clientApproval =
						list.find((a) => (a.authorRoles ?? []).some((r) => r?.toLowerCase() === "client")) ?? null;
					if (!cancelled) {
						setClientApprovals((prev) => ({ ...prev, [assignmentId]: clientApproval }));
					}
				} catch (error) {
					console.error(`Error fetching client approvals for assignment ${assignmentId}:`, error);
					if (!cancelled) {
						setClientApprovals((prev) => ({ ...prev, [assignmentId]: null }));
					}
				}
			}),
		);

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [creators, isClientOnly, canManage, campaignId, blockId]);

	const handleTabChange = (creatorId: string, tab: TabType) => {
		setActiveTabs((prev) => ({ ...prev, [creatorId]: tab }));
	};

	const renderContentRows = (entries: Array<[string, any]>, creator?: Creator) => {
		if (entries.length === 0) {
			return (
				<ContentRow>
					<ContentLabel>Info</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>No data available</ContentValue>
				</ContentRow>
			);
		}

		const rendered = new Set<string>();

		return entries.flatMap(([key, value]) => {
			if (isClientOnly) {
				if (!isRenderableValue(value)) return [];
				if (Array.isArray(value) && value.length === 0) return [];
				if (value === "" || value === "N/A") return [];
				const displayVal = formatDisplayValue(key, value);
				if (displayVal === "N/A") return [];
			}

			// Deduplicate aliased fields
			if (key === "category" || key === "categories") {
				if (rendered.has("category")) return [];
				rendered.add("category");
			}
			if (key === "creatorTier" || key === "tier") {
				if (rendered.has("tier")) return [];
				rendered.add("tier");
			}
			// Special handling for audienceAgeDistribution: aggregate percentages by ageRange
			if (
				key === "audienceAgeDistribution" &&
				Array.isArray(value) &&
				value.length > 0 &&
				value.every((v) => v && typeof v === "object" && "ageRange" in v && "percentage" in v)
			) {
				// Aggregate percentages by ageRange
				const aggregated = value.reduce((acc: { [ageRange: string]: number }, item: any) => {
					const ageRange = item.ageRange;
					const percentage = Number(item.percentage) || 0;
					if (acc[ageRange]) {
						acc[ageRange] += percentage;
					} else {
						acc[ageRange] = percentage;
					}
					return acc;
				}, {});

				// Convert aggregated object to array and sort by ageRange
				const aggregatedArray = Object.entries(aggregated)
					.map(([ageRange, percentage]) => ({ ageRange, percentage }))
					.sort((a, b) => {
						// Sort by the first number in the age range (e.g., "13-17" -> 13)
						const aStart = parseInt(a.ageRange.split("-")[0]);
						const bStart = parseInt(b.ageRange.split("-")[0]);
						return aStart - bStart;
					});

				return aggregatedArray.map((item: any, index: number) => (
					<ContentRow key={`${key}-${item.ageRange}-${index}`}>
						<ContentLabel>{`Audience Age ${item.ageRange} %`}</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{formatPercentage(Number(item.percentage))}</ContentValue>
					</ContentRow>
				));
			}

			// Special handling for sampleContent: display platform names as clickable links
			if (key === "sampleContent" || key === "sampleContents") {
				const urls = Array.isArray(value)
					? value.filter((v: any) => typeof v === "string" && looksLikeUrl(v))
					: typeof value === "string" && looksLikeUrl(value)
						? [value]
						: [];

				if (urls.length > 0) {
					return (
						<ContentRow key={key}>
							<ContentLabel>{formatLabel(key)}</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								{urls.map((url: string, index: number) => (
									<React.Fragment key={index}>
										{index > 0 && " | "}
										<a href={url} target="_blank" rel="noopener noreferrer">
											{getPlatformNameFromUrl(url)}
										</a>
									</React.Fragment>
								))}
							</ContentValue>
						</ContentRow>
					);
				}
				// If not a URL, fall through to default rendering
			}

			// Category field: render as dropdown
			if (key === "category" || key === "categories") {
				const currentVal = Array.isArray(value) ? value[0] || "" : typeof value === "string" ? value : "";
				if (!isClientOnly && canManage && onCategoryTierChange && creator) {
					return (
						<ContentRow key={key}>
							<ContentLabel>{formatLabel("category")}</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								<StyledSelect
									value={currentVal || undefined}
									onChange={(val: string) => {
										onCategoryTierChange(creator.id, "categories", val ? [val] : []);
									}}
									size="small"
									placeholder="Select"
									style={{ width: "100%" }}
									options={CREATOR_CATEGORIES.map((c) => ({ value: c, label: c }))}
									allowClear
								/>
							</ContentValue>
						</ContentRow>
					);
				}
				if (!currentVal) return [];
				return (
					<ContentRow key={key}>
						<ContentLabel>{formatLabel("category")}</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{currentVal}</ContentValue>
					</ContentRow>
				);
			}

			// Creator Tier field: render as dropdown when customizing
			if (key === "creatorTier" || key === "tier") {
				const currentVal = value || "";
				if (!isClientOnly && canManage && onCategoryTierChange && creator) {
					return (
						<ContentRow key={key}>
							<ContentLabel>Creator Tier</ContentLabel>
							<ContentSeparator>:</ContentSeparator>
							<ContentValue>
								<StyledSelect
									value={currentVal || undefined}
									onChange={(val: string) => {
										onCategoryTierChange(creator.id, "tier", val);
									}}
									size="small"
									placeholder="Select"
									style={{ width: "100%" }}
									options={CREATOR_TIERS.map((t) => ({ value: t, label: t }))}
									allowClear
								/>
							</ContentValue>
						</ContentRow>
					);
				}
				if (!currentVal) return [];
				return (
					<ContentRow key={key}>
						<ContentLabel>Creator Tier</ContentLabel>
						<ContentSeparator>:</ContentSeparator>
						<ContentValue>{currentVal}</ContentValue>
					</ContentRow>
				);
			}

			// Skip rationaleForProposing — replaced by recommendation from assignment
			if (key === "rationaleForProposing") {
				return [];
			}

			return (
				<ContentRow key={key}>
					<ContentLabel>{formatLabel(key)}</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						{Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string") ? (
							<List
								size="small"
								dataSource={value}
								split={false}
								renderItem={(item, index) => (
									<List.Item key={index} style={{ padding: 0, border: "none" }}>
										{looksLikeUrl(item) ? (
											<a href={item} target="_blank" rel="noopener noreferrer">
												{item}
											</a>
										) : (
											item
										)}
									</List.Item>
								)}
							/>
						) : (
							formatDisplayValue(key, value)
						)}
					</ContentValue>
				</ContentRow>
			);
		});
	};

	const renderReadOnlyRow = (label: string, value: React.ReactNode, key?: string) => (
		<ContentRow key={key || label}>
			<ContentLabel>{label}</ContentLabel>
			<ContentSeparator>:</ContentSeparator>
			<ContentValue>{value}</ContentValue>
		</ContentRow>
	);

	const renderContactEmailRow = (creator: Creator) => {
		const assignmentId = creator.assignment?.id || creator.assignmentId;
		const savedValue = creator.contactEmail ?? creator.assignment?.contactEmail ?? "";
		// The outreach panel is for picking creators: contact email stays editable in the Creator List only.
		if (!assignmentId || !canManage || isClientOnly || isInPanel) {
			return renderReadOnlyRow("Email Address", savedValue || "—", `${creator.id}-contact-email`);
		}
		return (
			<ContentRow key={`${creator.id}-contact-email`}>
				<ContentLabel>Email Address</ContentLabel>
				<ContentSeparator>:</ContentSeparator>
				<ContentValue>
					<ContactEmailField
						type="email"
						aria-label={`Campaign contact email for ${creator.name}`}
						$invalid={Boolean(contactEmail.errors[assignmentId])}
						disabled={contactEmail.saving[assignmentId]}
						value={contactEmail.drafts[assignmentId] ?? savedValue}
						onChange={(event) => contactEmail.setDraft(assignmentId, event.target.value)}
						onBlur={() => void contactEmail.save(assignmentId, savedValue)}
					/>
					{contactEmail.errors[assignmentId] && (
						<ContactEmailError>{contactEmail.errors[assignmentId]}</ContactEmailError>
					)}
				</ContentValue>
			</ContentRow>
		);
	};

	const renderBasicTab = (creator: Creator) => {
		const entries = Object.entries(creator).filter(([key]) => {
			if (RESERVED_BASIC_FIELDS.has(key)) {
				return false;
			}
			if (isHiddenRateField(key)) {
				return false;
			}
			if (Object.prototype.hasOwnProperty.call(PLATFORM_TABS, key)) {
				return false;
			}
			return typeof creator[key] !== "object" || Array.isArray(creator[key]);
		});

		const rows = renderContentRows(entries, creator);
		const showEditableFinalRate = isOutreach && canManage && !isClientOnly && !isInPanel;

		if (!showEditableFinalRate) {
			return (
				<>
					{renderContactEmailRow(creator)}
					{rows}
				</>
			);
		}

		return (
			<>
				{renderContactEmailRow(creator)}
				{rows}
				<ContentRow key={`${creator.id}-final-rate-edit`}>
					<ContentLabel>Final Rate</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						<RateInputShell $invalid={rateInvalid[creator.id]}>
							<RateSuffix>{finalRateCurrency}</RateSuffix>
							<RateTextInput
								inputMode="decimal"
								placeholder="0.00"
								value={
									rateDrafts[creator.id] ??
									formatRateDisplayValue(
										creator.finalRate ?? creator.assignment?.finalRate,
										finalRateCurrency,
									)
								}
								onChange={(e) => {
									const nextValue = sanitizeRateInputValue(e.target.value, finalRateCurrency);
									setRateDrafts((prev) => ({
										...prev,
										[creator.id]: formatRateDisplayValue(nextValue, finalRateCurrency),
									}));
									if (
										rateInvalid[creator.id] &&
										isRateInputValid(nextValue.trim(), finalRateCurrency)
									) {
										setRateInvalid((prev) => ({
											...prev,
											[creator.id]: false,
										}));
									}
									debouncedSaveFinalRates(creator);
								}}
							/>
						</RateInputShell>
					</ContentValue>
				</ContentRow>
				<ContentRow key={`${creator.id}-final-rate-converted-edit`}>
					<ContentLabel>Converted Rate</ContentLabel>
					<ContentSeparator>:</ContentSeparator>
					<ContentValue>
						<RateInputShell $invalid={convertedRateInvalid[creator.id]}>
							<RateSuffix>{finalRateConvertedCurrency}</RateSuffix>
							<RateTextInput
								inputMode="decimal"
								placeholder="0.00"
								value={
									convertedRateDrafts[creator.id] ??
									formatRateDisplayValue(
										creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
										finalRateConvertedCurrency,
									)
								}
								onChange={(e) => {
									const nextValue = sanitizeRateInputValue(
										e.target.value,
										finalRateConvertedCurrency,
									);
									setConvertedRateDrafts((prev) => ({
										...prev,
										[creator.id]: formatRateDisplayValue(nextValue, finalRateConvertedCurrency),
									}));
									if (
										convertedRateInvalid[creator.id] &&
										isRateInputValid(nextValue.trim(), finalRateConvertedCurrency)
									) {
										setConvertedRateInvalid((prev) => ({
											...prev,
											[creator.id]: false,
										}));
									}
									debouncedSaveFinalRates(creator);
								}}
							/>
						</RateInputShell>
					</ContentValue>
				</ContentRow>
			</>
		);
	};

	const renderPlatformTab = (creator: Creator, platformKey: string) => {
		const platformData = creator[platformKey];
		if (!platformData || typeof platformData !== "object") {
			return renderContentRows([]);
		}

		const entries = Object.entries(platformData);

		return renderContentRows(entries);
	};

	const renderTabContent = (creator: Creator, activeTab: TabType) => {
		if (activeTab === "basic") {
			return renderBasicTab(creator);
		}

		return renderPlatformTab(creator, activeTab);
	};

	const handleDragStart = (e: React.DragEvent, index: number) => {
		if (!isCustomizing) return;
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e: React.DragEvent, index: number) => {
		if (!isCustomizing || draggedIndex === null || draggedIndex === index) return;
		e.preventDefault();

		setDragOverIndex(index);

		// Reorder immediately during drag
		const newCreators = [...creators];
		const [draggedCreator] = newCreators.splice(draggedIndex, 1);
		newCreators.splice(index, 0, draggedCreator);

		onCreatorReorder?.(newCreators);
		setDraggedIndex(index); // Update dragged index to new position
	};

	const handleDragLeave = () => {
		setDragOverIndex(null);
	};

	const handleDrop = (e: React.DragEvent) => {
		if (!isCustomizing) return;
		e.preventDefault();
		setDraggedIndex(null);
		setDragOverIndex(null);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
		setDragOverIndex(null);
	};

	return (
		<GridContainer>
			<Grid>
				{creators.map((creator, index) => {
					const isVisible = creatorVisibility[creator.id] ?? true;
					const isDragging = draggedIndex === index;
					const isDragOver = dragOverIndex === index;

					const platformKeys = ["tiktok", "instagram", "facebook", "youtube"];

					const hasPlatformData = (platformData: any) => {
						if (platformData === null || platformData === undefined) return false;
						if (Array.isArray(platformData)) return platformData.length > 0;
						if (typeof platformData === "object") {
							for (const val of Object.values(platformData)) {
								if (isRenderableValue(val)) return true;
								if (Array.isArray(val) && val.length > 0) return true;
							}
							return Object.keys(platformData).length > 0;
						}
						return !!platformData;
					};

					const availablePlatforms = platformKeys.filter((p) => hasPlatformData((creator as any)[p]));
					const availablePlatformSet = new Set(availablePlatforms);
					const visibleActiveTab: TabType =
						activeTabs[creator.id] === "basic" || availablePlatformSet.has(activeTabs[creator.id] as string)
							? activeTabs[creator.id] || "basic"
							: "basic";

					return (
						<Card
							key={creator.id}
							$isVisible={isVisible}
							$isDragging={isDragging}
							$isDragOver={isDragOver}
							draggable={isCustomizing}
							onDragStart={(e) => handleDragStart(e, index)}
							onDragOver={(e) => handleDragOver(e, index)}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onDragEnd={handleDragEnd}
						>
							<CardHeader>
								<CardHeaderTop>
									<ProfileImage
										displaySize={80}
										src={creator.img_url || "/default_avatar.png"}
										alt={creator.name}
										onError={(e) => {
											const t = e.currentTarget;
											if (t.src.endsWith("/default_avatar.png")) return;
											t.src = "/default_avatar.png";
										}}
									/>
									<CreatorInfo>
										<NameRow>
											<NameWrap>
												<CreatorName>{creator.name}</CreatorName>
											</NameWrap>
											{(() => {
												const assignmentId =
													creator.assignment?.id || (creator as any).assignmentId;
												if (!assignmentId) return null;

												// Client view: show the client's own approval decision as a badge
												// in outreach, mirroring the agency card's approval badge.
												if (isClientOnly) {
													if (!isOutreach) return null;
													const approval = approvals[assignmentId];
													if (approval === undefined) return null;

													const clientStatus: "approved" | "rejected" | "pending" | "none" =
														approval === null
															? "none"
															: approval.status === "approved"
																? "approved"
																: approval.status === "rejected"
																	? "rejected"
																	: "pending";

													const clientLabel =
														clientStatus === "approved"
															? "Approved"
															: clientStatus === "rejected"
																? "Declined"
																: clientStatus === "pending"
																	? "Pending"
																	: "No Review";

													const clientIcon =
														clientStatus === "approved" ? (
															<CheckOutlined />
														) : clientStatus === "rejected" ? (
															<CloseOutlined />
														) : clientStatus === "pending" ? (
															<ClockCircleOutlined />
														) : (
															<MinusOutlined />
														);

													return (
														<ClientApprovalBadge
															$status={clientStatus}
															onClick={() => {
																const approvalForComment = approvals[assignmentId];
																setCommentTexts((prev) => ({
																	...prev,
																	[creator.id]: approvalForComment?.comment || "",
																}));
																setCardMode(
																	creator.id,
																	getCardMode(creator.id) === "comment"
																		? "basic"
																		: "comment",
																);
															}}
															title={
																clientStatus === "none"
																	? "No review yet"
																	: "View / edit your comment"
															}
														>
															{clientIcon}
															{clientLabel}
														</ClientApprovalBadge>
													);
												}

												if (!canManage) return null;
												const clientApproval = clientApprovals[assignmentId];

												// Still loading / not yet fetched — don't render a badge yet.
												if (clientApproval === undefined) return null;

												const status: "approved" | "rejected" | "pending" | "none" =
													clientApproval === null
														? "none"
														: clientApproval.status === "approved"
															? "approved"
															: clientApproval.status === "rejected"
																? "rejected"
																: "pending";

												const label =
													status === "approved"
														? "Approved"
														: status === "rejected"
															? "Declined"
															: status === "pending"
																? "Pending"
																: "No Review";

												const icon =
													status === "approved" ? (
														<CheckOutlined />
													) : status === "rejected" ? (
														<CloseOutlined />
													) : status === "pending" ? (
														<ClockCircleOutlined />
													) : (
														<MinusOutlined />
													);

												// In the outreach panel the tag is information only; the comment stays in the Creator List.
												if (isInPanel) {
													return (
														<ClientApprovalBadge
															as="span"
															$status={status}
															style={{ cursor: "default" }}
															title={
																status === "none" ? "No client review yet" : undefined
															}
														>
															{icon}
															{label}
														</ClientApprovalBadge>
													);
												}

												return (
													<ClientApprovalBadge
														$status={status}
														onClick={() => {
															setCardMode(
																creator.id,
																getCardMode(creator.id) === "client-comment"
																	? "basic"
																	: "client-comment",
															);
														}}
														title={
															status === "none"
																? "No client review yet"
																: "View client's comment"
														}
													>
														{icon}
														{label}
													</ClientApprovalBadge>
												);
											})()}
										</NameRow>
										<TierCategoryRow>
											{(creator.creatorTier || creator.tier) && (
												<Category>{creator.creatorTier || creator.tier}</Category>
											)}
											{creator.category && (
												<Category>
													{Array.isArray(creator.category)
														? creator.category.join(", ")
														: creator.category}
												</Category>
											)}
										</TierCategoryRow>
										{creator.profileUrl && (
											<ProfileLinkButton
												href={creator.profileUrl}
												target="_blank"
												rel="noopener noreferrer"
											>
												<CommentOutlined style={{ fontSize: 16, color: "#4460EF" }} />
												<span>View Profile</span>
											</ProfileLinkButton>
										)}
										{isClientOnly && !isOutreach && (
											<CreatorHeaderBottom>
												<SeeRecommendationsButton
													onClick={() => {
														setCardMode(
															creator.id,
															getCardMode(creator.id) === "recommendations"
																? "basic"
																: "recommendations",
														);
													}}
													$isActive={getCardMode(creator.id) === "recommendations"}
												>
													<RecommendationIcon
														$isActive={getCardMode(creator.id) === "recommendations"}
													>
														<CarryOutOutlined style={{ fontSize: 16 }} />
													</RecommendationIcon>
													<span>See Recommendations</span>
												</SeeRecommendationsButton>
											</CreatorHeaderBottom>
										)}
									</CreatorInfo>
									{!isClientOnly && (!isOutreach || isCustomizing) && (
										<SettingsIcon
											name={isCustomizing ? "moving" : "setting"}
											alt={isCustomizing ? "Drag to reorder" : "Settings"}
											$isCustomizing={isCustomizing}
											onClick={() => !isCustomizing && onSettingsClick?.(creator.id)}
										/>
									)}
								</CardHeaderTop>
								{getCardMode(creator.id) === "basic" &&
									isClientOnly &&
									creator.socialAccounts &&
									Object.keys(creator.socialAccounts).length > 0 && (
										<ChannelsSection>
											<ChannelsLabel>Creator's Channels</ChannelsLabel>
											<ChannelsIcons>
												{["tiktok", "instagram", "facebook", "youtube"].map((platform) => {
													const account = creator.socialAccounts?.[platform];
													if (!account) return null;
													return (
														<ChannelIcon
															key={platform}
															$platform={platform}
															href={account.url || "#"}
															target="_blank"
															rel="noopener noreferrer"
															onClick={(e) => {
																if (!account.url) e.preventDefault();
															}}
														>
															<ChannelIconImage
																name={platform as IconName}
																alt={platform}
																preserveOriginalColor
															/>
														</ChannelIcon>
													);
												})}
											</ChannelsIcons>
										</ChannelsSection>
									)}
							</CardHeader>

							{getCardMode(creator.id) === "basic" && (
								<TabsContainer $isVisible={isVisible}>
									<Tab
										$active={visibleActiveTab === "basic"}
										$isVisible={isVisible}
										onClick={() => handleTabChange(creator.id, "basic")}
									>
										<TabIcon
											name="profile"
											alt="Basic Info"
											$active={visibleActiveTab === "basic"}
										/>
										Basic Info
									</Tab>
									{availablePlatforms.map((platform) => (
										<Tab
											key={platform}
											$active={visibleActiveTab === platform}
											$isVisible={isVisible}
											onClick={() => handleTabChange(creator.id, platform as TabType)}
										>
											<TabIcon
												name={platform as IconName}
												alt={PLATFORM_TABS[platform as Exclude<TabType, "basic">]}
												preserveOriginalColor
												$active={visibleActiveTab === platform}
											/>
											{PLATFORM_TABS[platform as Exclude<TabType, "basic">]}
										</Tab>
									))}
								</TabsContainer>
							)}

							<CardContent>
								{getCardMode(creator.id) === "client-comment" ? (
									(() => {
										const assignmentId = creator.assignment?.id || (creator as any).assignmentId;
										const clientApproval = assignmentId ? clientApprovals[assignmentId] : null;
										const commentText = (clientApproval?.comment || "").trim();

										return (
											<ClientCommentReadOnlyContainer>
												<CommentBoxDivider />
												<CommentBoxHeader>
													<CommentBoxTitle>Client&apos;s Comment</CommentBoxTitle>
													<CommentBoxCloseButton
														onClick={() => setCardMode(creator.id, "basic")}
													>
														<CloseOutlined style={{ fontSize: 14 }} />
														<span>Close</span>
													</CommentBoxCloseButton>
												</CommentBoxHeader>
												{clientApproval?.authorName && (
													<ClientCommentAuthorRow>
														{clientApproval.authorImageUrl ? (
															<ClientCommentAuthorAvatar
																displaySize={24}
																src={clientApproval.authorImageUrl}
																alt={clientApproval.authorName}
															/>
														) : null}
														<span>{clientApproval.authorName}</span>
													</ClientCommentAuthorRow>
												)}
												{commentText ? (
													<ClientCommentReadOnlyBody>{commentText}</ClientCommentReadOnlyBody>
												) : (
													<ClientCommentEmptyState>
														Client didn&apos;t leave a comment
													</ClientCommentEmptyState>
												)}
											</ClientCommentReadOnlyContainer>
										);
									})()
								) : getCardMode(creator.id) === "recommendations" ? (
									<RecommendationsContainer>
										<RecommendationsDivider />
										<RecommendationsHeader>
											<RecommendationsTitle>Recommendations</RecommendationsTitle>
											<RecommendationsCloseButton
												onClick={() => setCardMode(creator.id, "basic")}
											>
												<CloseOutlined style={{ fontSize: 16 }} />
												<span>Close</span>
											</RecommendationsCloseButton>
										</RecommendationsHeader>
										{(() => {
											const recommendations: string[] = creator.recommendation || [];
											if (
												recommendations.length === 0 ||
												recommendations.every((r) => !r.trim())
											) {
												return <RecommendationBody>No recommendations yet.</RecommendationBody>;
											}
											return recommendations
												.filter((r) => r.trim())
												.map((rec, index) => (
													<div key={index}>
														<RecommendationStatus>
															<Icon
																name="greenChecked"
																size={24}
																style={{ flexShrink: 0 }}
															/>
															<RecommendationStatusText>
																Recommended
															</RecommendationStatusText>
														</RecommendationStatus>
														<RecommendationBody>{rec}</RecommendationBody>
													</div>
												));
										})()}
									</RecommendationsContainer>
								) : getCardMode(creator.id) === "comment" ? (
									<CommentBoxContainer>
										<CommentBoxDivider />
										<CommentBoxHeader>
											<CommentBoxTitle>Leave a Comment</CommentBoxTitle>
											<CommentBoxCloseButton onClick={() => setCardMode(creator.id, "basic")}>
												<CloseOutlined style={{ fontSize: 14 }} />
												<span>Cancel</span>
											</CommentBoxCloseButton>
										</CommentBoxHeader>
										<CommentBoxTextAreaWrapper>
											<CommentBoxTextArea
												placeholder="Add a comment"
												value={commentTexts[creator.id] || ""}
												onChange={(e) =>
													setCommentTexts((prev) => ({
														...prev,
														[creator.id]: e.target.value,
													}))
												}
											/>
											<CommentBoxSendIcon
												disabled={submittingComment[creator.id]}
												onClick={async () => {
													const assignmentId =
														creator.assignment?.id || (creator as any).assignmentId;
													const campaignId = searchParams.get("campaignId");
													const blockId = searchParams.get("blockId");
													if (!campaignId || !blockId || !assignmentId) return;
													setSubmittingComment((prev) => ({ ...prev, [creator.id]: true }));
													try {
														const approval =
															await creatorListAPI.updateCreatorAssignmentApprovalComment(
																campaignId,
																blockId,
																assignmentId,
																commentTexts[creator.id] || "",
															);
														setApprovals((prev) => ({ ...prev, [assignmentId]: approval }));
														message.success("Comment saved");
														setCardMode(creator.id, "basic");
													} catch (error: any) {
														message.error(
															error?.response?.data?.message || "Failed to save comment",
														);
													} finally {
														setSubmittingComment((prev) => ({
															...prev,
															[creator.id]: false,
														}));
													}
												}}
											>
												<SendOutlined />
											</CommentBoxSendIcon>
										</CommentBoxTextAreaWrapper>
									</CommentBoxContainer>
								) : (
									renderTabContent(creator, visibleActiveTab)
								)}
							</CardContent>

							<CardFooter $isVisible={isVisible}>
								{isInPanel ? (
									<PanelActionsContainer>
										{(() => {
											// Use a local override so optimistic updates do not flicker if parent props rerender.
											const isInOutreach =
												panelInListOverrides[creator.id] ?? creator.inList === true;

											const handleAddToOutreach = async () => {
												const urlCampaignId = searchParams.get("campaignId");
												const urlBlockId = searchParams.get("blockId");
												if (!urlCampaignId || !urlBlockId || !outreachId) {
													message.error("Missing campaign, block, or outreach information");
													return;
												}
												if (!creator.assignment?.id) {
													message.error("Missing assignment information");
													return;
												}
												if (pendingPanelActions[creator.id]) {
													return;
												}

												setPendingPanelActions((prev) => ({ ...prev, [creator.id]: true }));
												setPanelInListOverrides((prev) => ({ ...prev, [creator.id]: true }));
												onAddToList?.(creator.id);
												message.success("Creator added to outreach successfully!");
												try {
													await creatorListAPI.addCreatorToOutreach(
														urlCampaignId,
														urlBlockId,
														outreachId,
														creator.assignment.id,
													);
												} catch (error: any) {
													console.error("Error adding creator to outreach:", error);
													setPanelInListOverrides((prev) => ({
														...prev,
														[creator.id]: false,
													}));
													onRemoveFromList?.(creator.id);
													message.error(
														error?.response?.data?.error ||
															error?.message ||
															"Failed to add creator to outreach",
													);
												} finally {
													setPendingPanelActions((prev) => ({
														...prev,
														[creator.id]: false,
													}));
												}
											};

											const handleRemoveFromOutreach = async () => {
												const urlCampaignId = searchParams.get("campaignId");
												const urlBlockId = searchParams.get("blockId");
												if (!urlCampaignId || !urlBlockId) {
													message.error("Missing campaign or block information");
													return;
												}
												if (!creator.assignment?.id) {
													message.error("Missing assignment information");
													return;
												}
												if (pendingPanelActions[creator.id]) {
													return;
												}

												setPendingPanelActions((prev) => ({ ...prev, [creator.id]: true }));
												setPanelInListOverrides((prev) => ({ ...prev, [creator.id]: false }));
												onRemoveFromList?.(creator.id);
												try {
													await creatorListAPI.removeCreatorFromOutreach(
														urlCampaignId,
														urlBlockId,
														creator.assignment.id,
													);
													message.success("Creator removed from outreach successfully!");
												} catch (error: any) {
													console.error("Error removing creator from outreach:", error);
													setPanelInListOverrides((prev) => ({
														...prev,
														[creator.id]: true,
													}));
													onAddToList?.(creator.id);
													message.error(
														error?.response?.data?.error ||
															error?.message ||
															"Failed to remove creator from outreach",
													);
												} finally {
													setPendingPanelActions((prev) => ({
														...prev,
														[creator.id]: false,
													}));
												}
											};

											return isInOutreach ? (
												<RemoveFromListButton
													onClick={handleRemoveFromOutreach}
													disabled={pendingPanelActions[creator.id]}
												>
													{pendingPanelActions[creator.id] ? (
														"Removing..."
													) : (
														<>
															<MinusOutlined /> Remove from the Outreach
														</>
													)}
												</RemoveFromListButton>
											) : (
												<AddToListButton
													onClick={handleAddToOutreach}
													disabled={pendingPanelActions[creator.id]}
												>
													{pendingPanelActions[creator.id] ? (
														"Adding..."
													) : (
														<>
															<PlusOutlined /> Add Creator
														</>
													)}
												</AddToListButton>
											);
										})()}
										<PanelToggleWrapper>
											<VisibleLabel>Visible to Client</VisibleLabel>
											<SmallToggle
												$active={creatorVisibility[creator.id] || false}
												$disabled={false}
												onClick={() => onCreatorVisibilityToggle(creator.id)}
											/>
										</PanelToggleWrapper>
									</PanelActionsContainer>
								) : isClientOnly ? (
									<FooterRow>
										{isOutreach ? (
											(() => {
												const reachoutStatus = normalizeCreatorInterestStatus(
													creator.creatorInterested,
												);
												const display = REACHOUT_STATUS_DISPLAY[reachoutStatus ?? "none"];
												return (
													<ClientReachoutStatus>
														<span style={{ color: display.color, display: "inline-flex" }}>
															{display.icon}
														</span>
														{display.label}
													</ClientReachoutStatus>
												);
											})()
										) : (
											<Dropdown
												overlay={
													<ApprovalPopup>
														{[
															{
																key: "approve",
																label: "Approve",
																icon: (
																	<CheckCircleOutlined
																		style={{ color: "#52c41a", fontSize: 22 }}
																	/>
																),
															},
															{
																key: "pending",
																label: "Pending",
																icon: (
																	<ClockCircleOutlined
																		style={{ color: "#8c8c8c", fontSize: 22 }}
																	/>
																),
															},
															{
																key: "decline",
																label: "Decline",
																icon: (
																	<CloseCircleOutlined
																		style={{ color: "#ff4d4f", fontSize: 22 }}
																	/>
																),
															},
														].map(({ key, label, icon }) => (
															<ApprovalOption
																key={key}
																onClick={() => {
																	const assignmentId =
																		creator.assignment?.id ||
																		(creator as any).assignmentId;
																	const effectiveCampaignId =
																		searchParams.get("campaignId");
																	const effectiveBlockId =
																		searchParams.get("blockId");
																	if (
																		!effectiveCampaignId ||
																		!effectiveBlockId ||
																		!assignmentId
																	) {
																		message.error("Missing required information");
																		return;
																	}
																	const statusMap: {
																		[k: string]:
																			| "approved"
																			| "rejected"
																			| "pending";
																	} = {
																		approve: "approved",
																		decline: "rejected",
																		pending: "pending",
																	};
																	const previousApproval = approvals[assignmentId];
																	const optimisticApproval = {
																		id: previousApproval?.id || "temp",
																		assignmentId,
																		userId: "",
																		status: statusMap[key],
																		comment: previousApproval?.comment || "",
																		createdAt: new Date().toISOString(),
																		updatedAt: new Date().toISOString(),
																	} as CreatorAssignmentApproval;
																	setApprovals((prev) => ({
																		...prev,
																		[assignmentId]: optimisticApproval,
																	}));
																	setApprovalDropdownOpen((prev) => ({
																		...prev,
																		[creator.id]: false,
																	}));
																	creatorListAPI
																		.createCreatorAssignmentApproval(
																			effectiveCampaignId,
																			effectiveBlockId,
																			assignmentId,
																			statusMap[key],
																		)
																		.then((approval) => {
																			setApprovals((prev) => ({
																				...prev,
																				[assignmentId]: approval,
																			}));
																		})
																		.catch((error: any) => {
																			setApprovals((prev) => ({
																				...prev,
																				[assignmentId]:
																					previousApproval ?? null,
																			}));
																			message.error(
																				error?.response?.data?.message ||
																					"Failed to create approval",
																			);
																		});
																}}
															>
																{icon}
																<span className="label">{label}</span>
															</ApprovalOption>
														))}
													</ApprovalPopup>
												}
												trigger={["click"]}
												open={approvalDropdownOpen[creator.id] || false}
												onOpenChange={(open) =>
													setApprovalDropdownOpen((prev) => ({
														...prev,
														[creator.id]: open,
													}))
												}
												placement="topLeft"
											>
												{(() => {
													const assignmentId =
														creator.assignment?.id || (creator as any).assignmentId;
													const approval = assignmentId ? approvals[assignmentId] : null;
													const isLoading = assignmentId
														? loadingApprovals[assignmentId]
														: false;

													if (isLoading) {
														return <div style={{ height: 24 }} />;
													}

													if (approval) {
														const statusConfig = {
															approved: {
																icon: (
																	<CheckCircleOutlined
																		style={{ fontSize: 24, color: "#52c41a" }}
																	/>
																),
																text: "Approved",
															},
															rejected: {
																icon: (
																	<CloseCircleOutlined
																		style={{ fontSize: 24, color: "#ff4d4f" }}
																	/>
																),
																text: "Declined",
															},
															pending: {
																icon: (
																	<ClockCircleOutlined
																		style={{ fontSize: 24, color: "#8c8c8c" }}
																	/>
																),
																text: "Pending",
															},
														};
														const config =
															statusConfig[
																approval.status as keyof typeof statusConfig
															] || statusConfig.pending;

														return (
															<ClientActionText>
																{config.icon}
																{config.text}
															</ClientActionText>
														);
													}

													return (
														<ClientActionText>
															<CheckCircleOutlined
																style={{ fontSize: 24, color: "#4460EF" }}
															/>
															Approval
														</ClientActionText>
													);
												})()}
											</Dropdown>
										)}
										{!isOutreach && (
											<ClientActionText
												onClick={() => {
													const assignmentId =
														creator.assignment?.id || (creator as any).assignmentId;
													const approval = assignmentId ? approvals[assignmentId] : null;
													setCommentTexts((prev) => ({
														...prev,
														[creator.id]: approval?.comment || "",
													}));
													setCardMode(
														creator.id,
														getCardMode(creator.id) === "comment" ? "basic" : "comment",
													);
												}}
											>
												<CommentOutlined
													style={{
														fontSize: 24,
														color:
															getCardMode(creator.id) === "comment"
																? "#F72586"
																: "#4460EF",
													}}
												/>
												<span
													style={{
														color:
															getCardMode(creator.id) === "comment"
																? "#F72586"
																: undefined,
													}}
												>
													Leave a Comment
												</span>
											</ClientActionText>
										)}
									</FooterRow>
								) : canManage ? (
									<FooterRow>
										{isOutreach && (
											<CreatorInterestSelect
												creatorId={creator.id}
												assignmentId={creator.assignment?.id || (creator as any).assignmentId}
												campaignId={campaignId}
												blockId={blockId}
												interested={creator.creatorInterested}
												onInterestChange={onInterestChange}
												showLabel={true}
												yesNoLabels
												labelText="Interest"
												placeholderText="Select"
												inlineLabel
											/>
										)}
										{!isOutreach && (
											<WriteRationaleButton
												onClick={() => {
													const assignmentId =
														creator.assignment?.id || (creator as any).assignmentId;
													if (!assignmentId) {
														message.error("Missing assignment");
														return;
													}
													setRationale({
														creatorId: creator.id,
														assignmentId,
														text: (creator.recommendation || [])[0] || "",
													});
												}}
											>
												<Icon name="edit5" size={24} color="#4460EF" />
												Write Rationale
											</WriteRationaleButton>
										)}
										<ToggleWrapper>
											<SmallToggle
												$active={creatorVisibility[creator.id] || false}
												$disabled={false}
												onClick={() => onCreatorVisibilityToggle(creator.id)}
											/>
											<VisibleLabel>Visible to Client</VisibleLabel>
										</ToggleWrapper>
									</FooterRow>
								) : null}
							</CardFooter>
						</Card>
					);
				})}
			</Grid>
			{/* Rationale modal */}
			{rationale && (
				<RationaleModal
					open={!!rationale}
					initialValue={rationale.text}
					onCancel={() => setRationale(null)}
					loading={submittingRationale}
					onSave={async (text: string) => {
						if (!rationale) return;
						const campaignId = searchParams.get("campaignId");
						const blockId = searchParams.get("blockId");
						if (!campaignId || !blockId) {
							message.error("Missing campaign or block");
							return;
						}
						setSubmittingRationale(true);
						try {
							await creatorListAPI.updateRecommendation(campaignId, blockId, rationale.assignmentId, [
								text,
							]);
							message.success("Rationale saved");
							if (onUpdateCreator) {
								onUpdateCreator({ id: String(rationale.creatorId), recommendation: [text] });
							}
							setRationale(null);
						} catch (error: any) {
							message.error(error?.response?.data?.message || "Failed to save rationale");
						} finally {
							setSubmittingRationale(false);
						}
					}}
				/>
			)}
		</GridContainer>
	);
};
